import { NavLink, type To } from "react-router-dom";

import { cn } from "./cn.js";

/**
 * One resource, several faces. The tab strip is an address bar for the thing already
 * on screen: each face is its own URL, so a configuration screen can be linked to,
 * reloaded and gone back from like anything else.
 *
 * It scrolls rather than wraps on a narrow screen — a strip that reflows into two
 * rows moves the content under it every time the operator changes tab.
 */
export interface TabItem {
  to: string;
  label: string;
  /** Set when this face has something to say — a count, a warning dot. */
  badge?: string;
}

export function Tabs({ items }: { items: readonly TabItem[] }) {
  return (
    <nav className="-mx-4 overflow-x-auto border-b border-border px-4 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <ul className="flex min-w-max items-center gap-1">
        {items.map((item) => (
          <li key={item.to}>
            <NavLink
              className={({ isActive }) =>
                cn(
                  "-mb-px flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium whitespace-nowrap transition-colors",
                  isActive
                    ? "border-accent text-ink"
                    : "border-transparent text-ink-3 hover:text-ink-2",
                )
              }
              end
              to={item.to as To}
            >
              {item.label}
              {item.badge ? (
                <span className="rounded-full bg-surface-hi px-1.5 py-0.5 font-mono text-[0.625rem] leading-none text-ink-3 tabular-nums">
                  {item.badge}
                </span>
              ) : null}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
